import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from './ui';
import { adminAPI } from '../services/api';

const Management = () => {
  const [globalAdmins, setGlobalAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [adminsError, setAdminsError] = useState('');

  useEffect(() => {
    loadGlobalAdmins();
  }, []);

  const loadGlobalAdmins = async () => {
    try {
      setLoadingAdmins(true);
      const data = await adminAPI.getGlobalAdmins();
      setGlobalAdmins(data.global_admins);
    } catch (err) {
      setAdminsError(err.error || err.message || 'Failed to load global admins');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const sections = [
    {
      title: 'Users & Access',
      items: [
        { icon: '👥', label: 'User Management', desc: 'Global view: All users across all companies (global admin only)', route: '/admin' },
        { icon: '🛡️', label: 'Roles', desc: 'Define roles, permissions, and MFA requirements', route: '/roles' },
        { icon: '🏢', label: 'Companies', desc: 'Manage companies, service types, and allowed roles', route: '/companies' },
      ],
    },
    {
      title: 'Applications',
      items: [
        { icon: '📱', label: 'Applications', desc: 'Register apps, assign roles, configure SAML/OIDC', route: '/applications' },
        { icon: '📂', label: 'Categories', desc: 'Organize applications into categories (also editable on My Apps)', route: '/categories' },
        { icon: '📜', label: 'Certificates', desc: 'Manage SAML signing certificates', route: '/certificates' },
      ],
    },
    {
      title: 'System',
      items: [
        { icon: '🔒', label: 'Security', desc: 'Login activity, failed attempts, vulnerability scanning', route: '/security' },
        { icon: '📋', label: 'System Logs', desc: 'View audit trail and system events', route: '/system-logs' },
        { icon: '🔑', label: 'API Keys', desc: 'Manage API keys for service-to-service authentication', route: '/api-keys' },
        { icon: '🔐', label: 'Microsoft OAuth', desc: 'Configure Microsoft OAuth provider settings', route: '/microsoft-oauth' },
      ],
    },
  ];

  return (
    <PageLayout title="Management" subtitle="Admin tools and configuration">
      {/* Global Admins Section */}
      <div className="mb-10 animate-fade">
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">Global Administrators</span>
          <span className="text-xs text-gray-400">
            {loadingAdmins ? 'Loading...' : `${globalAdmins.length} admin${globalAdmins.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {adminsError ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <p className="text-sm text-red-700">{adminsError}</p>
          </div>
        ) : loadingAdmins ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <p className="text-sm text-gray-400">Loading global administrators...</p>
          </div>
        ) : globalAdmins.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <p className="text-sm text-gray-400">No global administrators found.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Email</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Role</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Companies</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {globalAdmins.map((admin) => (
                  <tr key={admin.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-800">
                        {admin.firstname || ''} {admin.lastname || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{admin.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {admin.roles.map((role, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {admin.companies.map((company, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            {company}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        admin.is_active
                          ? 'bg-green-50 text-green-700 border border-green-100'
                          : 'bg-red-50 text-red-700 border border-red-100'
                      }`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sections.map((section) => (
        <div key={section.title} className="mb-10 animate-fade">
          <div className="flex items-center mb-5">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">{section.title}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.items.map((item) => (
              <Link
                key={item.route}
                to={item.route}
                className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 group-hover:border-primary-light flex items-center justify-center text-2xl mr-4 transition-all shadow-inner">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-800 text-base tracking-normal truncate group-hover:text-primary transition-colors">{item.label}</h3>
                  </div>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{item.desc}</p>
                <div className="mt-4 flex justify-end">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary group-hover:text-primary-hover transition-colors">
                    Open Settings
                    <svg className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </PageLayout>
  );
};

export default Management;
